import { XIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Modal as AriaModal,
  Dialog,
  Heading,
  ModalOverlay,
} from 'react-aria-components';
import { Button } from './Button';
import { cn } from './lib/cn';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export type ModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode | ((opts: { close: () => void }) => ReactNode);
  title?: ReactNode;
  /** Sits along the bottom, separated by a rule: the dialog's actions. */
  footer?: ReactNode;
  /** `xl` and `full` are for tables and pickers that need the width. */
  size?: ModalSize;
  /** Puts an X in the header that closes it, as Esc and the backdrop do. */
  showCloseButton?: boolean;
  /** Set false while an action is in flight, to keep Esc and the backdrop from closing it. */
  isDismissable?: boolean;
  'aria-label'?: string;
  className?: string;
  bodyClassName?: string;
};

const SIZES: Record<ModalSize, string> = {
  sm: 'sk:max-w-96',
  md: 'sk:max-w-128',
  lg: 'sk:max-w-160',
  xl: 'sk:max-w-270',
  full: 'sk:max-w-full',
};

/** Focus-trapping surface over the page, for work that must be settled first. */
export function Modal({
  isOpen,
  onOpenChange,
  children,
  title,
  footer,
  size = 'sm',
  showCloseButton = false,
  isDismissable = true,
  'aria-label': ariaLabel,
  className,
  bodyClassName,
}: ModalProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={!isDismissable}
      className={cn(
        'sk:fixed sk:inset-0 sk:z-3000 sk:flex sk:items-center sk:justify-center sk:p-4',
        'sk:bg-black/25',
      )}
    >
      <AriaModal
        className={cn(
          'sk:w-full sk:rounded-card sk:inset-shadow-hairline',
          'sk:bg-surface sk:shadow-card sk:outline-none',
          SIZES[size],
          className,
        )}
      >
        <Dialog
          aria-label={title ? undefined : ariaLabel}
          className="sk:flex sk:flex-col sk:outline-none"
        >
          {({ close }) => (
            <>
              {title || showCloseButton ? (
                <header
                  className={cn(
                    'sk:flex sk:items-center sk:gap-2 sk:px-4 sk:py-3',
                    title ? 'sk:border-b sk:border-rule' : 'sk:justify-end',
                  )}
                >
                  {title ? (
                    <Heading
                      slot="title"
                      className="sk:m-0 sk:min-w-0 sk:flex-1 sk:text-sm sk:font-semibold sk:text-fg"
                    >
                      {title}
                    </Heading>
                  ) : null}
                  {showCloseButton ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      shape="circle"
                      aria-label="Close"
                      className="sk:shrink-0"
                      isDisabled={!isDismissable}
                      onPress={close}
                    >
                      <XIcon size={12} weight="bold" aria-hidden />
                    </Button>
                  ) : null}
                </header>
              ) : null}

              <div
                className={cn('sk:p-4 sk:text-dense sk:text-fg', bodyClassName)}
              >
                {typeof children === 'function'
                  ? children({ close })
                  : children}
              </div>

              {footer ? (
                <footer className="sk:flex sk:flex-wrap sk:items-center sk:justify-end sk:gap-2 sk:border-t sk:border-rule sk:px-4 sk:py-3">
                  {footer}
                </footer>
              ) : null}
            </>
          )}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}
